import * as React from 'react';

import type { W_MaterialCost_FindForWorkSummary_VM_Single } from 'ab-api/web/materialCost/findForWorkSummary';

import { createExistsMap } from '@acceligentllc/shared/utils/array';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import Radio from 'af-components/Controls/Radio';
import SearchInput, { useSearchInputState } from 'af-components/Controls/SearchInput';

import CostSourceFilter from './CostSourceFilter';
import { MaterialCostEntry } from './MaterialCostEntry';
import styles from './styles.module.scss';
import TagRadio from './TagRadio';
import { useMaterialCostSourceFilterContext } from '../../../MaterialCostSourceFilterContext';

const COST_SOURCE_ORDERING = {
	[MaterialCostSource.JOB_MATERIAL]: 0,
	[MaterialCostSource.JOB]: 1,
	[MaterialCostSource.COMPANY_MATERIAL]: 2,
	[MaterialCostSource.COMPANY]: 3,
};

function sortCost(cost1: W_MaterialCost_FindForWorkSummary_VM_Single, cost2: W_MaterialCost_FindForWorkSummary_VM_Single) {
	const displayNameDiff = cost1.displayName.localeCompare(cost2.displayName);
	if (displayNameDiff !== 0) {
		return displayNameDiff;
	}
	return COST_SOURCE_ORDERING[cost1.source] - COST_SOURCE_ORDERING[cost2.source];
}

const COST_SOURCES = [MaterialCostSource.JOB_MATERIAL, MaterialCostSource.JOB, MaterialCostSource.COMPANY_MATERIAL, MaterialCostSource.COMPANY];

function createSearchFilter(search: string) {
	return (_mat: W_MaterialCost_FindForWorkSummary_VM_Single) => {
		return (
			_mat.displayName.toLowerCase().includes(search.toLowerCase())
			|| _mat.description.toLowerCase().includes(search.toLowerCase())
			|| _mat.vendor?.toLowerCase().includes(search.toLowerCase())
			// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
			|| _mat.vendorUniqueId?.toLowerCase().includes(search.toLowerCase())
		);
	};
}

interface OwnProps {
	materialCostsBySource: Record<MaterialCostSource, W_MaterialCost_FindForWorkSummary_VM_Single[]>;
	onDate: string;
	onSelect: (materialCost: W_MaterialCost_FindForWorkSummary_VM_Single) => void;
	targetUnit: Nullable<QuantityUnitType>;
}

type Props = OwnProps;

export const MaterialCosts: React.FC<Props> = (props) => {
	const { materialCostsBySource, onDate, onSelect, targetUnit } = props;

	const { filters } = useMaterialCostSourceFilterContext();

	const searchInputState = useSearchInputState();

	const [filterTag, setFilterTag] = React.useState<Nullable<string>>(null);

	const materialCosts = React.useMemo(() => {
		return Object.entries(materialCostsBySource).reduce<W_MaterialCost_FindForWorkSummary_VM_Single[]>((_acc, [key, materials]) => {
			if (filters[key]) {
				const searchedMaterials = searchInputState.value
					? materials.filter(createSearchFilter(searchInputState.value))
					: materials;

				_acc.push(...searchedMaterials);
			}
			return _acc;
		}, []);
	}, [filters, materialCostsBySource, searchInputState.value]);

	const materialCostsForDisplay = React.useMemo(() => {
		const costs = materialCosts.filter((_cost) => !filterTag || _cost.tags.includes(filterTag));
		return costs.sort(sortCost);
	}, [filterTag, materialCosts]);

	const isEmpty = !materialCostsForDisplay.length;

	const emptyMessage = !searchInputState.value
		? 'No applicable Material Costs found.'
		: 'No Material Costs match the search criteria.';

	const renderMaterialCost = React.useCallback((materialCost: W_MaterialCost_FindForWorkSummary_VM_Single) => {
		return (
			<MaterialCostEntry materialCost={materialCost} onDate={onDate} onSelect={onSelect} targetUnit={targetUnit} />
		);
	}, [onDate, onSelect, targetUnit]);

	const renderSourceFilter = React.useCallback((source: MaterialCostSource) => {
		return (
			<CostSourceFilter source={source} />
		);
	}, []);

	const availableTagsMap = React.useMemo(() => {
		return createExistsMap(materialCosts.flatMap((_mc) => _mc.tags), (_tag) => _tag);
	}, [materialCosts]);

	React.useEffect(() => {
		if (!filterTag || filterTag in availableTagsMap) {
			return;
		}
		setFilterTag(null);
	}, [availableTagsMap, filterTag]);

	const tagFilters = React.useMemo(() => {
		return Object.keys(availableTagsMap).sort((t1, t2) => t1.localeCompare(t2));
	}, [availableTagsMap]);

	const handleSelectAllTag = React.useCallback(() => {
		setFilterTag(null);
	}, []);

	const renderTagFilter = React.useCallback((tag: string) => {
		return (
			<TagRadio className={styles.tagItem} isChecked={tag === filterTag} onSelect={setFilterTag} tag={tag} />
		);
	}, [filterTag]);

	return (
		<div className={styles.content}>
			<div className={styles.tagsPane}>
				<div className={styles.title}>Tags:</div>
				<div className={styles.tagFilterList}>
					<Radio checked={!filterTag} className={styles.tagItem} label="All" onCheck={handleSelectAllTag} />
					{tagFilters.map(renderTagFilter)}
				</div>
			</div>
			<div className={styles.costsPane}>
				<div className={styles.searchInputContainer}>
					<SearchInput {...searchInputState} placeholder="Search" />
				</div>
				<div className={styles.sourceFilters}>
					{COST_SOURCES.map(renderSourceFilter)}
				</div>
				{!isEmpty
					? (
						<div className={styles.costsList}>
							{materialCostsForDisplay.map(renderMaterialCost)}
						</div>
					) : (
						<div className={styles.emptyMessageContainer}>
							<div className={styles.message}>{emptyMessage}</div>
						</div>
					)}
			</div>
		</div>
	);
};
